import { useApiQuery, useApiMutation } from "../services/react-query.js";
import { keepPreviousData, useMutation } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { getAccessToken } from "../services/authStore";
import { callApi } from "../services/api";

export const useGetAllTickets = (params) => {
  return useApiQuery({
    queryOptions: {
      queryKey: ["tickets", params],
    },
    fetchOptions: {
      url: "/tickets",
      method: "GET",
      params,
    },
  });
};

export const findTicketsWithName = async ({ eventId, firstName, lastName }) => {
  const params = new URLSearchParams({
    eventId,
    limit: 100,
    filter: JSON.stringify({ firstName, lastName }),
  });
  const { data } = await callApi({
    url: `/tickets?${params}`,
    method: "GET",
    token: localStorage.getItem("token"),
  });
  const sameName = (a, b) =>
    a?.trim().toLowerCase() === b?.trim().toLowerCase();
  return data.filter(
    (ticket) =>
      sameName(ticket.firstName, firstName) &&
      sameName(ticket.lastName, lastName)
  );
};

// Several tickets may share an email (one person receiving a team's codes);
// saved, but worth a second look.
const sharedEmailMessage = (data) =>
  data?.sharedEmail
    ? {
        color: "yellow",
        title: "Saved. This email already has a ticket for this event",
        message: "Each ticket gets its own email and code.",
      }
    : null;

export const useCreateTicket = () => {
  return useApiMutation({
    invalidateQueryKeys: [["tickets"]],
    fetchOptions: {
      url: "/tickets",
      method: "POST",
    },
    successMessage: (data) =>
      sharedEmailMessage(data) || { title: "Ticket was created successfully" },
  });
};

export const useUpdateTicket = ({ ticketId }) => {
  return useApiMutation({
    invalidateQueryKeys: [["tickets"], ["customFields"]],
    fetchOptions: {
      url: `/tickets/${ticketId}`,
      method: "PATCH",
    },
    successMessage: (data) =>
      sharedEmailMessage(data) || { title: "Ticket was updated successfully" },
  });
};

export const useGetTicket = ({ ticketId }) => {
  return useApiQuery({
    queryOptions: {
      queryKey: ["tickets", ticketId],
    },
    fetchOptions: {
      url: `/tickets/${ticketId}`,
      method: "GET",
    },
  });
};
export const useImportTickets = () => {
  return useApiMutation({
    invalidateQueryKeys: [["tickets"]],
    fetchOptions: {
      url: "/tickets/import",
      method: "POST",
    },
    successMessage: {
      title: "Tickets were imported successfully",
    },
  });
};

export const useValidateTicketImport = () => {
  return useApiMutation({
    fetchOptions: {
      url: "/tickets/import/validate",
      method: "POST",
    },
    hideNotification: true,
  });
};

export const useGetTicketHistory = (params) => {
  return useApiQuery({
    queryOptions: {
      queryKey: ["ticketHistory", params],
      placeholderData: keepPreviousData,
    },
    fetchOptions: {
      url: `/tickets/history`,
      method: "GET",
      params,
    },
  });
};

export const useGetTicketInfo = ({ eventId }) => {
  return useApiQuery({
    queryOptions: { queryKey: ["ticketInfo", eventId], enabled: !!eventId },
    fetchOptions: {
      url: `/tickets/register/summary`,
      method: "GET",
      params: {
        eventId,
      },
    },
  });
};

export const useDeleteTicket = () =>
  useApiMutation({
    mutationOptions: { mutationKey: "useDeleteTicket" },
    fetchOptions: { url: `/tickets`, method: "DELETE" },
    invalidateQueryKeys: [["tickets"]],
    successMessage: { title: "Ticket has been deleted", color: "green" },
  });

export const useGetTicketColumns = ({ eventId }) => {
  return useApiQuery({
    queryOptions: {
      queryKey: ["columns", eventId],
    },
    fetchOptions: {
      url: `/tickets/columns/${eventId}`,
      method: "GET",
    },
  });
};

export const useUpdateColumnVisibility = ({ eventId, columns }) => {
  return useApiMutation({
    invalidateQueryKeys: [["columns"]],
    fetchOptions: {
      url: `/tickets/columns/${eventId}`,
      method: "PATCH",
      body: { columns },
    },
  });
};

export const useGetCustomFields = ({ eventId, ticketId }) => {
  return useApiQuery({
    queryOptions: { queryKey: ["customFields", eventId, ticketId] },
    fetchOptions: {
      url: `/tickets/custom-columns/${eventId}`,
      method: "GET",
      params: ticketId ? { ticketId } : undefined,
    },
  });
};

export const useUpdateCustomFields = ({ eventId, ticketId, data }) => {
  return useApiMutation({
    invalidateQueryKeys: [["tickets"], ["customFields"]],
    fetchOptions: {
      url: `/tickets/custom-columns/${eventId}`,
      method: "PATCH",
      body: { ticketId, data },
    },
    successMessage: {
      title: "Custom fields updated successfully",
    },
  });
};

export const useExportTickets = () => {
  return useMutation({
    queryKey: ["exportAttendees"],
    mutationFn: async ({ eventId }) => {
      const token = await getAccessToken();
      const response = await fetch(`/api/tickets/export?eventId=${eventId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "text/csv",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to export tickets");
      }

      return response.blob().then((blob) => ({
        blob,
        eventId,
      }));
    },
    onSuccess: ({ blob, eventId }) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `congrs-tickets-${eventId}-${new Date().toDateString()}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      notifications.show({
        title: "Attendees exported successfully",
        color: "green",
      });
    },
    onError: () => {
      notifications.show({
        title: "Failed to export tickets",
        color: "red",
      });
    },
  });
};

// Ticket claims (docs/features/auth-sessions-and-ticket-claims.md). Both
// change what the ticket view and the table show, so both refetch tickets.
// Call `mutate({})`: `callApi` always declares a JSON body, and Fastify
// rejects a JSON request whose body is empty.

/**
 * Issue a new claim code. The old code and its link stop working, and the
 * ticket counts as not emailed again, for "Send ticket emails".
 */
export const useRotateClaimCode = ({ ticketId }) =>
  useApiMutation({
    invalidateQueryKeys: [["tickets"], ["ticketEmailCount"]],
    fetchOptions: { url: `/tickets/${ticketId}/claim-code`, method: "POST" },
    successMessage: {
      title: "New ticket code issued",
      message: "Email the ticket or share the new link.",
    },
  });

/**
 * Take the ticket off the account holding it; anonymous sessions opened for
 * it are signed out. It keeps its code unless called with `{ newCode: true }`.
 */
export const useUnclaimTicket = ({ ticketId }) =>
  useApiMutation({
    invalidateQueryKeys: [["tickets"], ["ticketHistory"], ["ticketEmailCount"]],
    fetchOptions: { url: `/tickets/${ticketId}/unclaim`, method: "POST" },
    successMessage: (_data, variables) =>
      variables?.newCode
        ? {
            title: "Ticket taken out, with a new code",
            message:
              "The old code and link stop working. Email the ticket or share the new link.",
          }
        : {
            title: "Ticket taken out of the app",
            message:
              "They can add it again with the code or link in their ticket email.",
          },
  });

/**
 * "Email the ticket": the default ticket email to this one ticket, at its own
 * email, again if it had it already.
 */
export const useSendTicketEmail = ({ ticketId }) =>
  useApiMutation({
    invalidateQueryKeys: [["tickets"], ["ticketEmailCount"]],
    fetchOptions: { url: `/tickets/${ticketId}/ticket-email`, method: "POST" },
    successMessage: { title: "Ticket email sent", color: "green" },
  });

/** How many tickets have not had the default ticket email yet. */
export const useGetTicketEmailCount = ({ eventId }) =>
  useApiQuery({
    queryOptions: {
      queryKey: ["ticketEmailCount", eventId],
      enabled: !!eventId,
    },
    fetchOptions: {
      url: `/events/${eventId}/tickets/ticket-emails`,
      method: "GET",
    },
  });

/**
 * "Send ticket emails": the default ticket email to every ticket with an email
 * that has not had it, at the ticket's own email.
 */
export const useSendTicketEmails = ({ eventId }) =>
  useApiMutation({
    // The confirmation reports how many went out.
    hideNotification: true,
    invalidateQueryKeys: [["ticketEmailCount"], ["tickets"]],
    fetchOptions: {
      url: `/events/${eventId}/tickets/ticket-emails`,
      method: "POST",
    },
  });
